import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useXFetch } from '~/shared/utils/useXFetch';
import { toaster } from '~/shared/utils/toaster';

import type { AutoscaleRule, AutoscaleRulesResponse } from '../../hooks/useAutoscaleRulesQuery';

const baseUrl = '/api/v1/volumes/autoscaling/rules';

interface UseToggleRuleProps {
  onSuccess?: () => void;
  onError?: () => void;
}

type AutoscaleRulesQueries = [string[], AutoscaleRulesResponse | undefined][];

export function useToggleRule({ onSuccess, onError }: UseToggleRuleProps) {
  const api = useXFetch();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      rule,
      newActiveState,
    }: {
      rule: AutoscaleRule;
      newActiveState: boolean;
    }) => {
      const params = new URLSearchParams();
      if (rule.accountId) {
        params.set('accounts', rule.accountId);
      }
      const url = params.size ? `${baseUrl}/${rule.ruleId}?${params.toString()}` : `${baseUrl}/${rule.ruleId}`;
      const response = await api(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          active: newActiveState,
          rule: rule.rawRule,
        }),
      });
      return response;
    },
    onMutate: async ({ rule, newActiveState }) => {
      const autoscaleRulesQueryKey = ['autoscaleRules'];
      // Match the exact shape used by useAutoscaleRuleDetails so getQueryData /
      // setQueryData actually hit the right cache entry.
      const autoscaleRuleDetailsQueryKey = [
        'autoscaleRuleDetails',
        rule.ruleId,
        rule.accountId ?? null,
      ];

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: autoscaleRulesQueryKey, exact: false });
      await queryClient.cancelQueries({ queryKey: autoscaleRuleDetailsQueryKey });

      // Snapshot previous values
      const previousRuleDetails = queryClient.getQueryData<AutoscaleRule>(
        autoscaleRuleDetailsQueryKey
      );
      const previousAutoscaleRulesQueries = queryClient.getQueriesData<AutoscaleRulesResponse>({
        queryKey: autoscaleRulesQueryKey,
        exact: false,
      });

      if (previousRuleDetails) {
        queryClient.setQueryData<AutoscaleRule>(autoscaleRuleDetailsQueryKey, {
          ...previousRuleDetails,
          enabled: newActiveState,
        });
      }

      queryClient.setQueriesData<AutoscaleRulesResponse>(
        { queryKey: autoscaleRulesQueryKey, exact: false },
        (oldData) => {
          if (!oldData) {
            return oldData;
          }
          return {
            ...oldData,
            rules: oldData.rules.map((r) =>
              r.ruleId === rule.ruleId ? { ...r, enabled: newActiveState } : r
            ),
          };
        }
      );

      return { previousRuleDetails, previousAutoscaleRulesQueries };
    },
    onSuccess: (_, { newActiveState, rule }) => {
      toaster({
        variant: 'success',
        title: newActiveState ? 'Rule Activated' : 'Rule Deactivated',
        message: `Rule ${rule.ruleId} has been successfully turned ${newActiveState ? 'on' : 'off'}.`,
        toastId: rule.ruleId,
      });
    },
    onError: (err, { newActiveState, rule }, context) => {
      // Rollback to the previous value on error
      if (context?.previousAutoscaleRulesQueries) {
        for (const [
          queryKey,
          data,
        ] of context.previousAutoscaleRulesQueries as AutoscaleRulesQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousRuleDetails) {
        queryClient.setQueryData(
          ['autoscaleRuleDetails', rule.ruleId, rule.accountId ?? null],
          context.previousRuleDetails
        );
      }
      toaster({
        variant: 'error',
        title: 'Error',
        message: `Rule ${rule.ruleId} failed to turn ${newActiveState ? 'on' : 'off'}. Please try again.`,
        toastId: rule.ruleId,
      });
    },
    onSettled: async (data, error, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['autoscaleRules'], exact: false }),
        // Prefix match — covers any accountId scoping. Picked up by the lazy
        // useAutoscaleRuleDetails query once the side panel has opened its gate.
        queryClient.invalidateQueries({
          queryKey: ['autoscaleRuleDetails', variables.rule.ruleId],
          exact: false,
        }),
      ]);

      if (error) {
        onError?.();
      } else {
        onSuccess?.();
      }
    },
  });

  return {
    toggleRule: mutation.mutate,
    isMutating: mutation.isPending,
  };
}
